import {
  Download,
  Image as ImageIcon,
  RefreshCw,
  Shield,
  Upload,
  User,
} from "lucide-react";
import useVirtualTryOn from "../hooks/useVirtualTryOn";

/**
 * The try-on mirror: the right-hand panel of the try-on stage on product
 * pages (TryOnStage). It shows the picker, then the render, then the result,
 * with "Try another" and "Download" beneath. Header, product photo and the
 * way out belong to the stage.
 *
 * @param {object} props
 * @param {string} props.productId
 * @param {string|null} [props.autoStart] Render immediately in this mode (agent path).
 */
export default function TryOnMirror({ productId, autoStart = null }) {
  const vto = useVirtualTryOn({ open: true, articleId: productId, autoStart });

  const {
    step,
    resultImage,
    imageError,
    error,
    showUploadArea,
    setShowUploadArea,
    personImagePreview,
    fileInputRef,
  } = vto;

  return (
        <div className="ob-tryon">
          <div
            className={`ob-mirror ob-tryon-mirror ${step === 2 ? "" : "ob-mirror-still"}`}
            data-step={step}
            aria-live="polite"
          >
            {/* 1 · choose */}
            {step === 1 && !showUploadArea && (
              <div className="ob-tryon-choose">
                <div className="ob-tryon-glyph">
                  <User size={22} />
                </div>
                <div className="ob-tryon-h">See it on you</div>
                <p className="ob-tryon-p">
                  Pick how you&rsquo;d like to try it, or just ask Orbiee{" "}
                  <em>&ldquo;how does this look on me?&rdquo;</em>
                </p>

                <div className="ob-tryon-opts">
                  <button
                    type="button"
                    className="ob-tryon-opt ob-tryon-opt-primary"
                    onClick={() => vto.start("custom_model")}
                  >
                    <User />
                    <span>
                      <b>Use my profile picture</b>
                      <small>Fastest &mdash; nothing to upload</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="ob-tryon-opt"
                    onClick={() => setShowUploadArea(true)}
                  >
                    <Upload />
                    <span>
                      <b>Upload a photo</b>
                      <small>A full-length shot gives the truest fit</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="ob-tryon-opt"
                    onClick={() => vto.start("default")}
                  >
                    <ImageIcon />
                    <span>
                      <b>Use a default model</b>
                      <small>Just see the garment on a body</small>
                    </span>
                  </button>
                </div>

                <div className="ob-tryon-note">
                  <Shield size={11} />
                  JPG or PNG &middot; up to 10MB &middot; 100% private
                </div>
              </div>
            )}

            {/* 1b · upload */}
            {step === 1 && showUploadArea && (
              <div className="ob-tryon-choose">
                <div className="ob-tryon-glyph">
                  <Upload size={22} />
                </div>
                <div className="ob-tryon-h">Upload your photo</div>
                <p className="ob-tryon-p">
                  A clear, full-length photo facing the camera works best.
                </p>
                {personImagePreview && (
                  <img
                    src={personImagePreview}
                    alt="Preview of you"
                    className="ob-tryon-preview"
                  />
                )}
                <div className="ob-tryon-opts">
                  <button
                    type="button"
                    className="ob-tryon-opt ob-tryon-opt-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload />
                    <span>
                      <b>{personImagePreview ? "Change photo" : "Choose a photo"}</b>
                      <small>It renders as soon as it&rsquo;s in</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="ob-tryon-link"
                    onClick={() => setShowUploadArea(false)}
                  >
                    Back to options
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={vto.handleFileSelect}
                  className="hidden"
                />
              </div>
            )}

            {/* 2 · rendering */}
            {step === 2 && (
              <div className="ob-tryon-choose">
                <div className="ob-tryon-spinner" aria-hidden="true" />
                <div className="ob-tryon-h">Rendering your try-on</div>
                <p className="ob-tryon-p">This takes a few moments.</p>
              </div>
            )}

            {/* 3 · result */}
            {step === 3 &&
              (resultImage ? (
                imageError ? (
                  <div className="ob-tryon-choose">
                    <div className="ob-tryon-glyph" style={{ color: "#ff8f8f" }}>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.7}
                        strokeLinecap="round"
                        className="w-6 h-6"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v5M12 16.2v.1" />
                      </svg>
                    </div>
                    <div className="ob-tryon-h">We couldn&rsquo;t load the image</div>
                    <p className="ob-tryon-p">
                      A network hiccup or an expired link, most likely.
                    </p>
                    <div className="ob-tryon-opts">
                      <button
                        type="button"
                        className="ob-tryon-opt ob-tryon-opt-primary"
                        onClick={vto.retryImage}
                      >
                        <RefreshCw />
                        <span>
                          <b>Try loading again</b>
                        </span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <img
                      src={resultImage}
                      alt="Your virtual try-on"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      className="ob-tryon-result"
                      onLoad={vto.onResultLoaded}
                      onError={vto.onResultError}
                    />
                    <span className="ob-tryon-resultbadge">Your try-on</span>
                  </>
                )
              ) : (
                <div className="ob-tryon-choose">
                  <div className="ob-tryon-h">No result available</div>
                </div>
              ))}
          </div>

          {error && step !== 2 && (
            <div className="ob-alert mt-3" role="alert">
              {error}
            </div>
          )}

          {step === 3 && (
            <div className="ob-tryon-actions">
              <button
                type="button"
                className="ob-btn ob-btn-quiet ob-btn-sm"
                onClick={vto.tryAnother}
              >
                <RefreshCw size={14} />
                Try another
              </button>
              <button
                type="button"
                className="ob-btn ob-btn-ghost ob-btn-sm"
                onClick={vto.download}
                disabled={!resultImage}
              >
                <Download size={14} />
                Download
              </button>
            </div>
          )}
        </div>
  );
}
